import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Divider, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import PageContainer from '../layouts/PageContainer';
import Button from '../components/Button';
import CheckoutAddresses from '../components/CheckoutAddresses';
import CheckoutSlots, { slotToSummaryPayload } from '../components/CheckoutSlots';
import CheckoutPaymentSelect from '../components/CheckoutPaymentSelect';
import CheckoutStockGate from '../components/CheckoutStockGate';
import CheckoutCouponModal from '../components/CheckoutCouponModal';
import { storageGet } from '../platform/storage';
import {
  V2_PAYMENT_TYPE,
  deliveryDayToYyyyMmDd,
  findOosCartLines,
  getBranchStockMap,
  getCheckoutTimeSlots,
} from '../services/checkout';
import { clearCartCoupon, getAppliedCouponDiscountRupee, getCart } from '../services/commerce';

const LOCATION_KEY = 'v2_location';

export default function Checkout() {
  const navigate = useNavigate();

  const [storeId, setStoreId] = useState(null);
  const [deliveryDay, setDeliveryDay] = useState('today');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState('');
  const [selectedSlotId, setSelectedSlotId] = useState(null);

  const [stockMap, setStockMap] = useState({});
  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState('');
  const [paymentType, setPaymentType] = useState(V2_PAYMENT_TYPE.COD);

  const deliveryDate = useMemo(() => deliveryDayToYyyyMmDd(deliveryDay), [deliveryDay]);

  const refreshStoreFromCache = useCallback(async () => {
    const loc = await storageGet(LOCATION_KEY);
    setStoreId(loc?.storeId ? String(loc.storeId) : null);
  }, []);

  useEffect(() => {
    refreshStoreFromCache();
  }, [refreshStoreFromCache]);

  const handleAfterLocationMerge = useCallback(async ({ storeId: sid }) => {
    setStoreId(sid ? String(sid) : null);
    setSelectedSlotId(null);
    setSlots([]);
    setSlotsError('');
    setStockMap({});
  }, []);

  useEffect(() => {
    if (!storeId) {
      setSlots([]);
      setSelectedSlotId(null);
      setSlotsLoading(false);
      setSlotsError('');
      return;
    }

    let cancelled = false;
    (async () => {
      setSlotsLoading(true);
      setSlotsError('');
      try {
        const list = await getCheckoutTimeSlots({ storeId, deliveryDate });
        if (cancelled) return;
        setSlots(list);
        setSelectedSlotId(null);
      } catch (e) {
        if (!cancelled) {
          setSlots([]);
          setSlotsError(e?.message || 'Could not load time slots.');
        }
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [storeId, deliveryDate]);

  useEffect(() => {
    if (!storeId) {
      setStockMap({});
      setStockLoading(false);
      setStockError('');
      return;
    }

    let cancelled = false;
    (async () => {
      setStockLoading(true);
      setStockError('');
      try {
        const map = await getBranchStockMap(storeId);
        if (!cancelled) setStockMap(map);
      } catch (e) {
        if (!cancelled) {
          setStockMap({});
          setStockError(e?.message || 'Could not check stock.');
        }
      } finally {
        if (!cancelled) setStockLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [storeId]);

  const [cartSnapshot, setCartSnapshot] = useState({
    products: [],
    totalAmount: 0,
    totalCount: 0,
    coupon: null,
    instructions: '',
  });
  const [couponModalOpen, setCouponModalOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cart = await getCart();
      if (!cancelled) setCartSnapshot(cart);
    })();
    const onCart = () => {
      getCart().then((c) => setCartSnapshot(c));
    };
    window.addEventListener('v2-cart-changed', onCart);
    return () => {
      cancelled = true;
      window.removeEventListener('v2-cart-changed', onCart);
    };
  }, []);

  const oosLines = useMemo(
    () => findOosCartLines(stockMap, cartSnapshot.products),
    [stockMap, cartSnapshot.products]
  );

  const hasOos = oosLines.length > 0;

  const selectedSlot = useMemo(
    () => slots.find((s) => String(s.id) === String(selectedSlotId)) ?? null,
    [slots, selectedSlotId]
  );

  const allSlotsDisabled = useMemo(
    () =>
      slots.length > 0 &&
      slots.every((s) => Boolean(s.isDisabled) || s.active === false),
    [slots]
  );

  const canContinue =
    Boolean(storeId) &&
    Boolean(selectedSlot) &&
    !selectedSlot.isDisabled &&
    selectedSlot.active !== false &&
    !stockLoading &&
    !stockError &&
    !hasOos;

  const subtotal = Number(cartSnapshot.totalAmount) || 0;
  const couponDiscount = getAppliedCouponDiscountRupee(cartSnapshot);
  const discountedSubtotal = Math.max(0, subtotal - couponDiscount);
  const hasCartItems =
    (Number(cartSnapshot.totalCount) || 0) > 0 &&
    Array.isArray(cartSnapshot.products) &&
    cartSnapshot.products.length > 0;

  const handleContinue = () => {
    if (!canContinue || !selectedSlot || hasOos) return;
    navigate('/v2/checkout/summary', {
      replace: false,
      state: {
        deliveryDate,
        timeSlot: slotToSummaryPayload(selectedSlot),
        paymentType,
        instructions: String(cartSnapshot.instructions ?? '').trim(),
      },
    });
  };

  const sectionPaperSx = {
    p: 2.5,
    mb: 2.5,
    borderRadius: 2,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'rgb(255, 240, 219)',
    boxShadow: 'none',
  };

  return (
    <PageContainer title="Checkout">
      <Paper elevation={0} sx={sectionPaperSx}>
        <CheckoutAddresses onAfterLocationMerge={handleAfterLocationMerge} />
      </Paper>

      <Paper elevation={0} sx={sectionPaperSx}>
        <CheckoutSlots
          storeId={storeId}
          deliveryDay={deliveryDay}
          onDeliveryDayChange={(d) => {
            setDeliveryDay(d);
            setSelectedSlotId(null);
          }}
          slots={slots}
          slotsLoading={slotsLoading}
          slotsError={slotsError}
          selectedSlotId={selectedSlotId}
          onSelectSlotId={setSelectedSlotId}
        />

        {storeId ? (
          <CheckoutStockGate
            oosLines={oosLines}
            loading={stockLoading}
            error={stockError}
            onGoToCart={() => navigate('/v2/cart')}
          />
        ) : null}
      </Paper>

      <Paper elevation={0} sx={sectionPaperSx}>
        <CheckoutPaymentSelect value={paymentType} onChange={setPaymentType} />
      </Paper>

      {hasCartItems ? (
        <Box
          sx={{
            mt: 2,
            mb: 1,
            p: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            bgcolor: 'background.paper',
          }}
        >
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
            Order estimate
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 0.75 }}>
            <Typography variant="body2" color="text.secondary">
              Items subtotal
            </Typography>
            <Typography variant="body1" fontWeight={700}>
              ₹{subtotal}
            </Typography>
          </Box>
          {couponDiscount > 0 && cartSnapshot.coupon?.couponCode ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 1,
                mb: 0.75,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Coupon ({cartSnapshot.coupon.couponCode})
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <Typography variant="body1" fontWeight={700} color="success.main">
                  −₹{couponDiscount}
                </Typography>
                <Button size="small" variant="text" onClick={() => clearCartCoupon()} sx={{ minWidth: 0, px: 0.5 }}>
                  Remove
                </Button>
              </Box>
            </Box>
          ) : null}
          {couponDiscount > 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Items after coupon
              </Typography>
              <Typography variant="body1" fontWeight={800}>
                ₹{discountedSubtotal}
              </Typography>
            </Box>
          ) : null}
          <Divider sx={{ my: 1 }} />
          {!cartSnapshot.coupon?.couponCode ? (
            <Button
              variant="text"
              onClick={() => setCouponModalOpen(true)}
              sx={{
                p: 0,
                minWidth: 0,
                justifyContent: 'flex-start',
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.875rem',
                textDecoration: 'underline',
                color: 'primary.main',
                mb: 1,
              }}
            >
              Apply coupon
            </Button>
          ) : null}
          <Typography variant="caption" color="text.secondary" display="block">
            Delivery fee and final total are shown on the next step.
          </Typography>
        </Box>
      ) : null}

      <CheckoutCouponModal
        open={couponModalOpen}
        onClose={() => setCouponModalOpen(false)}
        products={cartSnapshot.products}
        cartSubtotal={Number(cartSnapshot.totalAmount) || 0}
      />

      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button
          fullWidth
          size="large"
          disabled={!canContinue || hasOos}
          onClick={handleContinue}
          sx={{ borderRadius: '5px' }}
        >
          Continue to summary
        </Button>

        {!storeId ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Select an address with delivery available.
          </Typography>
        ) : stockLoading ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Checking stock…
          </Typography>
        ) : stockError ? (
          <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
            Could not verify stock. Try again or pick another address.
          </Typography>
        ) : hasOos ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Update quantities or remove items in your cart to continue.
          </Typography>
        ) : allSlotsDisabled && !slotsLoading && !slotsError ? null : !selectedSlotId ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
            Choose a delivery slot to continue.
          </Typography>
        ) : null}
      </Box>
    </PageContainer>
  );
}
